// ts-rest contract for the snapshot debug endpoint (issue #30, ADR-0006).
//
// This is the demoable use of the `snapshotResponse` envelope and the
// `?version=<ref>` query fragment that surface PRDs (#34, #40, #41, #43,
// #44) will copy when they implement their own snapshot-derived endpoints.
//
// The debug endpoint deliberately returns *no rows*: the per-file-type row
// Object Types are declared inside each surface PRD, not here. Wiring the
// helper end-to-end against an empty row fetcher proves the versionRef
// plumbing — default-to-active, explicit `?version`, and the
// `versionId: null` no-data-yet case — independently of any particular
// surface's row shape.

import { initContract } from '@ts-rest/core';
import { z } from 'zod';

import { snapshotResponse, SnapshotQuerySchema } from '../schemas/version-ref';

const c = initContract();

// The debug endpoint returns no rows (yet) — the surface PRDs will
// instantiate `snapshotResponse(...)` with their own row schema. We use
// `z.unknown()` here so the envelope shape is provable end-to-end without
// pretending to know the row shape for any particular fileType.
export const snapshotDebugResponseSchema = snapshotResponse(z.array(z.unknown()));
export type SnapshotDebugResponse = z.infer<typeof snapshotDebugResponseSchema>;

const errorBody = z.object({ error: z.string(), code: z.string().optional() });

export const snapshotContract = c.router(
    {
        /**
         * GET /api/snapshots/:fileType/:scope/rows
         *
         * Defaults to the active version named by the `(fileType, scope)`
         * `SnapshotPointer`. Pass `?version=<ParsedSnapshot.id>` to read a
         * specific historical version. The response wraps an (empty) rows
         * array together with the `versionRef` it resolved.
         */
        readDebug: {
            method: 'GET',
            path: '/snapshots/:fileType/:scope/rows',
            query: SnapshotQuerySchema,
            responses: {
                200: snapshotDebugResponseSchema,
                404: errorBody,
            },
            summary: 'Demo endpoint exercising the readSnapshot helper end-to-end (ADR-0006).',
        },
    },
    {
        pathPrefix: '/api',
        strictStatusCodes: false,
    },
);
